/**
 * Sign-in step one: request a code.
 *
 * The redirect target travels through the query string so a guarded page can
 * send the user back where they were after they are signed in.
 */

import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { isValidEmail } from '@shared/validation'
import { sanitizeRedirectPath } from '@shared/redirect'

import { PageHeader } from '../../components/layout/page-header'
import { ErrorAlert } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Field, FieldGroup } from '../../components/ui/field'
import { Input } from '../../components/ui/input'
import { useSendCode } from '../../hooks/use-auth'
import { currentLocale } from '../../lib/i18n'
import { mutationErrorText } from '../../lib/mutation-error'
import { withReferralParams } from '../../lib/referral-capture'

export function LoginEmailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sendCode = useSendCode()

  const [email, setEmail] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  const locale = currentLocale()
  // Sanitised once, with the rule the Worker also applies, so every link and
  // navigation built from it stays on this site.
  const redirect = sanitizeRedirectPath(searchParams.get('redirect'))

  function submit() {
    const value = email.trim()
    if (!isValidEmail(value)) {
      setLocalError(t('auth.invalidEmail'))
      return
    }
    setLocalError(null)

    sendCode.mutate(
      { email: value, locale },
      {
        onSuccess: (result) => {
          // Local development returns the account instead of a code: the visitor
          // is already signed in, so the destination is reached directly.
          if (result.user) {
            navigate(redirect, { replace: true })
            return
          }

          const params = new URLSearchParams({ email: value, redirect })
          // The test environment echoes the code, so the next step can display it
          // without a trip to the server logs.
          navigate(`/login/verify?${params.toString()}`, {
            state: result.developmentCode ? { developmentCode: result.developmentCode } : undefined,
          })
        },
      },
    )
  }

  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the form silent.
  const apiError = mutationErrorText(sendCode.error, t)

  return (
    <div className="container-narrow flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[{ label: t('nav.home'), to: '/' }, { label: t('auth.loginTitle') }]}
        title={t('auth.emailStepTitle')}
        description={t('auth.emailStepBody')}
      />

      <Card>
        <CardContent className="pt-4 sm:pt-5">
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <FieldGroup>
              <Field
                label={t('auth.emailLabel')}
                htmlFor="email"
                required
                error={localError}
                hint={t('auth.emailPlaceholder')}
              >
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  aria-invalid={Boolean(localError)}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>
            </FieldGroup>

            {apiError ? (
              <ErrorAlert message={apiError} />
            ) : null}

            <Button type="submit" disabled={sendCode.isPending}>
              {sendCode.isPending ? t('auth.sendingCode') : t('auth.sendCode')}
            </Button>
          </form>

          <div className="flex items-center gap-3">
            <span className="bg-border h-px flex-1" />
            <span className="text-muted-foreground text-xs">{t('auth.orDivider')}</span>
            <span className="bg-border h-px flex-1" />
          </div>

          {/*
            A full navigation, not a fetch: the Worker answers with a redirect to
            GitHub, which a browser has to follow itself. A captured referral code
            rides along, because the callback creates the account before the client
            ever sees a session.
          */}
          <Button variant="outline" asChild>
            <a href={withReferralParams('/api/auth/github/start', { redirect })}>
              {t('auth.githubLogin')}
            </a>
          </Button>
        </CardContent>
      </Card>

      <p className="text-muted-foreground text-xs">
        {t('home.betaBody')}{' '}
        <Link to="/guide" className="inline-block px-1 py-1.5 underline">
          {t('nav.guide')}
        </Link>
      </p>
    </div>
  )
}
